import KpiCard from "../../UI/KpiCard";
import React, { useEffect, useMemo } from "react";
import {
  formatNumberPct,
  formatNumber,
  formatDateYYYYMMDD,
} from "utils/formatting";
import { formatDurationFromMins } from "utils/formatting/timeFormatters";
import { getMondayOfCurrentWeek } from "../../../utils/common";
import { subDays } from "date-fns";
import { useHealthMetrics } from "../../../hooks/useFulfillmentsAnalytics";
import { useError } from "../../../contexts/ErrorContext";
import { Box, Typography } from "@mui/material";

interface ShippingWeeklyKpiCardsProps {
  dateStart: string;
  dateEnd: string;
}

const calculateTrend = (
  currentValue: number,
  previousValue: number
): number => {
  if (previousValue === 0) {
    return 0;
  }
  return ((currentValue - previousValue) / previousValue) * 100;
};

const ShippingWeeklyKpiCards: React.FC<ShippingWeeklyKpiCardsProps> = ({
  dateStart,
  dateEnd,
}) => {
  const { showError } = useError();

  const currWeekMonday = getMondayOfCurrentWeek();
  const earliestDataMonday = "2024-04-23";

  // Calculate date range for current week and previous week
  const thisWeekDateStart = dateStart;
  const lastWeekDateStart = useMemo(
    () => subDays(new Date(thisWeekDateStart), 7).toISOString().split("T")[0],
    [thisWeekDateStart]
  );
  const lastWeekDateEnd = useMemo(
    () => subDays(new Date(dateEnd), 7).toISOString().split("T")[0],
    [dateEnd]
  );

  // Early return for cases where we shouldn't show data
  if (thisWeekDateStart >= currWeekMonday) {
    return (
      <Box sx={{ marginTop: "25px" }}>
        <Typography variant="h5" sx={{ marginBottom: "15px" }}>
          Shipping Results
        </Typography>
        <Typography sx={{ fontStyle: "italic" }}>
          Shipping KPI data is not available until the Monday of the following
          week.
        </Typography>
      </Box>
    );
  }

  if (lastWeekDateStart < earliestDataMonday) {
    return (
      <Box sx={{ marginTop: "25px" }}>
        <Typography variant="h5" sx={{ marginBottom: "15px" }}>
          Shipping Results
        </Typography>
        <Typography sx={{ fontStyle: "italic" }}>
          No historical data to compare to.
        </Typography>
      </Box>
    );
  }

  // Only call hooks when we should query data
  return (
    <ShippingKpiCardsWithData
      dateStart={dateStart}
      dateEnd={dateEnd}
      lastWeekDateStart={lastWeekDateStart}
      lastWeekDateEnd={lastWeekDateEnd}
      showError={showError}
    />
  );
};

// Separate component that only renders when data should be queried
const ShippingKpiCardsWithData: React.FC<{
  dateStart: string;
  dateEnd: string;
  lastWeekDateStart: string;
  lastWeekDateEnd: string;
  showError: (message: string) => void;
}> = ({
  dateStart,
  dateEnd,
  lastWeekDateStart,
  lastWeekDateEnd,
  showError,
}) => {
  const {
    data: healthMetrics,
    error,
    isLoading,
  } = useHealthMetrics(formatDateYYYYMMDD(lastWeekDateStart), dateEnd);

  const thisWeekData = healthMetrics?.[1] || {};
  const lastWeekData = healthMetrics?.[0] || {};

  useEffect(() => {
    if (error) {
      showError(error?.message || `Failed to get weekly health metrics`);
    }
  }, [error, showError]);

  return (
    <Box sx={{ marginTop: "25px" }}>
      <Typography
        variant="h5"
        sx={{
          marginBottom: "15px",
          fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
        }}
      >
        Shipping Results
      </Typography>
      {isLoading || !healthMetrics ? (
        <Typography
          sx={{
            fontStyle: "italic",
            fontSize: { xs: "0.875rem", sm: "1rem" },
          }}
        >
          Loading KPI data...
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: { xs: "center", sm: "flex-start" },
            gap: { xs: 0.5, sm: 1 },
            "& > *": {
              flex: { xs: "1 1 calc(50% - 4px)", sm: "0 0 auto" },
              maxWidth: { xs: "calc(50% - 4px)", sm: "none" },
            },
          }}
        >
          <KpiCard
            title="Export FTQ"
            value={formatNumberPct(thisWeekData.pct_export_ftq_succeeded, 0)}
            trend={calculateTrend(
              thisWeekData.pct_export_ftq_succeeded,
              lastWeekData.pct_export_ftq_succeeded
            )}
            subtitle={`${thisWeekData.count_export_ftq_succeeded} / ${thisWeekData.count_scheduled_deliveries}`}
          />
          <KpiCard
            title="Export Avg Duration"
            value={formatDurationFromMins(thisWeekData.avg_duration_minutes)}
            trend={calculateTrend(
              thisWeekData.avg_duration_minutes,
              lastWeekData.avg_duration_minutes
            )}
            subtitle={`${formatDurationFromMins(
              thisWeekData.min_duration_minutes
            )} - ${formatDurationFromMins(thisWeekData.max_duration_minutes)}`}
            reverseTrend={true}
          />
          <KpiCard
            title="Max Volume"
            value={formatNumber(thisWeekData.max_row_count)}
            trend={calculateTrend(
              thisWeekData.max_row_count,
              lastWeekData.max_row_count
            )}
          />
          <KpiCard
            title="Total Volume"
            value={formatNumber(thisWeekData.total_row_count)}
            trend={calculateTrend(
              thisWeekData.total_row_count,
              lastWeekData.total_row_count
            )}
          />

          <Box sx={{ width: "100%", height: 0 }} />

          <KpiCard
            title="Connector Shipping (Fulfillments)"
            value={formatNumberPct(thisWeekData.pct_connector_fulfillments, 0)}
            trend={calculateTrend(
              thisWeekData.pct_connector_fulfillments,
              lastWeekData.pct_connector_fulfillments
            )}
            subtitle={`${thisWeekData.count_connector_fulfillments} / ${thisWeekData.count_fulfillments}`}
          />
          <KpiCard
            title="Connector Shipping (Customers)"
            value={formatNumberPct(thisWeekData.pct_connector_customers, 0)}
            trend={calculateTrend(
              thisWeekData.pct_connector_customers,
              lastWeekData.pct_connector_customers
            )}
            subtitle={`${thisWeekData.count_connector_customers} / ${thisWeekData.count_customers}`}
          />
          <KpiCard
            title="Connector FTQ"
            value={formatNumberPct(thisWeekData.pct_connector_ftq_succeeded, 0)}
            trend={calculateTrend(
              thisWeekData.pct_connector_ftq_succeeded,
              lastWeekData.pct_connector_ftq_succeeded
            )}
            subtitle={`${thisWeekData.count_connector_ftq_succeeded} / ${thisWeekData.count_connector_fulfillments}`}
          />
        </Box>
      )}
    </Box>
  );
};

export default ShippingWeeklyKpiCards;
